import cx from "classnames";
import { ReactNode } from "react";

import { AnimatedBackground } from "components/AnimatedBackground";
import { AnimatedBackgroundConfigType } from "components/AnimatedBackground/AnimatedBackground";

export interface MobileViewportContainerProps {
  /** Header content (back button, logo, etc.) */
  header?: ReactNode;
  /** Main content area */
  children: ReactNode;
  /** Bottom CTA/button area - will be sticky to bottom */
  bottomCta?: ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Background color override */
  backgroundColor?: string;
  /** Custom classes for the content area */
  contentClassName?: string;
  /** Whether to show the animated background */
  showAnimatedBackground?: boolean;
  /** Background configuration */
  backgroundConfig?: AnimatedBackgroundConfigType;
}

/**
 * Container component that provides consistent mobile viewport layout
 * for onboarding screens. Handles safe areas, mobile constraints,
 * and proper content/CTA positioning.
 */
export const MobileViewportContainer = ({
  header,
  children,
  bottomCta,
  className,
  backgroundColor = "bg-white",
  contentClassName,
  showAnimatedBackground = true,
  backgroundConfig,
}: MobileViewportContainerProps) => {
  const content = (
    <div
      className={cx(
        "mx-auto flex min-h-screen w-full max-w-[390px] flex-col",
        !showAnimatedBackground && backgroundColor,
        className
      )}
    >
      {/* Header section with safe area top */}
      {header && (
        <div className="shrink-0 pt-[env(safe-area-inset-top)]">{header}</div>
      )}

      {/* Main content area - takes remaining space with 56px top padding to match design */}
      <div className={cx("flex-1 px-8 pb-4 pt-[56px]", contentClassName)}>
        {children}
      </div>

      {/* Bottom padding area - adjusts based on bottomCta */}
      <div
        className={cx(
          "shrink-0 px-8",
          bottomCta
            ? "pb-[calc(2rem_+_env(safe-area-inset-bottom))]"
            : "pb-[env(safe-area-inset-bottom)]"
        )}
      >
        {bottomCta}
      </div>
    </div>
  );
  return showAnimatedBackground ? (
    <AnimatedBackground
      mode="light-prominent"
      backgroundConfig={backgroundConfig}
    >
      {content}
    </AnimatedBackground>
  ) : (
    content
  );
};

export default MobileViewportContainer;
